import { useState } from "react"
import { Box, Button, Divider, Popover, Stack, TextField, Typography } from "@mui/material"
import CalendarIcon from "@mui/icons-material/CalendarTodayOutlined"
import dayjs from "dayjs"

/**
 * A date range, mostly chosen rather than typed.
 *
 * ── Why the presets carry the weight ────────────────────────────────────────
 *
 * Almost every range anybody wants from a report is one of nine: this week,
 * last month, this quarter, last year. Typing two dates to express "last month"
 * is arithmetic a person should not be doing, and it is arithmetic they get
 * wrong at the edges — off-by-one on the last day is the classic, and it makes
 * a month-end total quietly short.
 *
 * So the presets are the interface and the two fields are the escape hatch, in
 * that order. The fields stay, because the tenth range always exists.
 *
 * ── Why a preset writes its dates into the fields ───────────────────────────
 *
 * Pressing "Last quarter" fills in 1 Jul – 30 Sep rather than storing a token
 * that means "last quarter". Two reasons: what got asked for is then visible
 * and checkable, and the range does not silently move under a report somebody
 * left open across a month boundary.
 */

/*
 * Ranges are computed at open time, not at module load.
 *
 * A tab left open overnight would otherwise still think "today" is yesterday —
 * and that is exactly the session where somebody checks the morning's hours and
 * finds none.
 */
export function presetRanges(now = dayjs()) {
  const d = (value) => value.format("YYYY-MM-DD")
  return [
    { id: "today", label: "Today", from: d(now), to: d(now) },
    { id: "this_week", label: "This Week", from: d(now.startOf("week")), to: d(now.endOf("week")) },
    {
      id: "last_week",
      label: "Last Week",
      from: d(now.subtract(1, "week").startOf("week")),
      to: d(now.subtract(1, "week").endOf("week")),
    },
    { id: "this_month", label: "This Month", from: d(now.startOf("month")), to: d(now.endOf("month")) },
    {
      id: "last_month",
      label: "Last Month",
      from: d(now.subtract(1, "month").startOf("month")),
      to: d(now.subtract(1, "month").endOf("month")),
    },
    /* dayjs has no quarter unit without a plugin, so the month is floored to a
       multiple of three. Cheaper than the plugin and impossible to get wrong. */
    { id: "this_quarter", label: "This Quarter", ...quarterOf(now) },
    { id: "last_quarter", label: "Last Quarter", ...quarterOf(now.subtract(3, "month")) },
    { id: "this_year", label: "This Year", from: d(now.startOf("year")), to: d(now.endOf("year")) },
    {
      id: "last_year",
      label: "Last Year",
      from: d(now.subtract(1, "year").startOf("year")),
      to: d(now.subtract(1, "year").endOf("year")),
    },
  ]
}

function quarterOf(moment) {
  const start = moment.month(Math.floor(moment.month() / 3) * 3).startOf("month")
  return { from: start.format("YYYY-MM-DD"), to: start.add(2, "month").endOf("month").format("YYYY-MM-DD") }
}

/** "Sep 1 – Sep 30, 2026", or the preset's name when the dates are exactly one. */
export function describeRange(from, to) {
  if (!from && !to) return "All time"
  const match = presetRanges().find((preset) => preset.from === from && preset.to === to)
  if (match) return match.label
  const start = from ? dayjs(from) : null
  const end = to ? dayjs(to) : null
  if (start && end) {
    const sameYear = start.year() === end.year()
    return `${start.format(sameYear ? "MMM D" : "MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`
  }
  if (start) return `From ${start.format("MMM D, YYYY")}`
  return `Until ${end.format("MMM D, YYYY")}`
}

export default function DateRangePicker({ from, to, onChange, label = "Date range" }) {
  const [anchor, setAnchor] = useState(null)
  const [draftFrom, setDraftFrom] = useState(from || "")
  const [draftTo, setDraftTo] = useState(to || "")

  const open = (event) => {
    /* Seeded from what is applied, so opening and cancelling cannot change
       anything, and a half-typed date is discarded rather than kept. */
    setDraftFrom(from || "")
    setDraftTo(to || "")
    setAnchor(event.currentTarget)
  }

  const apply = (nextFrom, nextTo) => {
    onChange({ from: nextFrom || null, to: nextTo || null })
    setAnchor(null)
  }

  /* Backwards is a typo, not a range. Swapped rather than refused: nobody means
     "from October to September", and an error message here is a lecture. */
  const applyDraft = () => {
    if (draftFrom && draftTo && draftFrom > draftTo) apply(draftTo, draftFrom)
    else apply(draftFrom, draftTo)
  }

  const presets = presetRanges()
  const activePreset = presets.find((preset) => preset.from === from && preset.to === to)

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        startIcon={<CalendarIcon sx={{ fontSize: 16 }} />}
        onClick={open}
        sx={{ minWidth: 200, justifyContent: "flex-start", fontWeight: 600 }}
        aria-label={`${label}: ${describeRange(from, to)}`}
      >
        {describeRange(from, to)}
      </Button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.75, width: 460, maxWidth: "94vw" } } }}
      >
        <Stack direction={{ xs: "column", sm: "row" }}>
          {/* The nine that cover almost everything, down the left the way every
              reporting tool puts them — it is where people look. */}
          <Stack sx={{ p: 1, minWidth: 150, bgcolor: "action.hover" }}>
            {presets.map((preset) => (
              <Button
                key={preset.id}
                size="small"
                color={activePreset?.id === preset.id ? "primary" : "inherit"}
                onClick={() => apply(preset.from, preset.to)}
                sx={{
                  justifyContent: "flex-start",
                  fontWeight: activePreset?.id === preset.id ? 700 : 500,
                  px: 1.25,
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Or pick the two ends
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={draftFrom}
                onChange={(event) => setDraftFrom(event.target.value)}
                fullWidth
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={draftTo}
                onChange={(event) => setDraftTo(event.target.value)}
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
              {/* Clear means all time, and says so rather than emptying two
                  boxes and leaving somebody to guess what that did. */}
              <Button size="small" color="inherit" onClick={() => apply("", "")}>
                All time
              </Button>
              <Button size="small" variant="contained" onClick={applyDraft}>
                Apply
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  )
}
